import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  Creative,
  CreativeFilters,
  CreativeFormData,
  CreativesResponse,
  CreativeUsage,
  UpdateCreativePayload,
} from "../../schemas/creativeSchema";

const GET_CREATIVES = gql`
  query GetCreatives(
    $page: Int
    $pageSize: Int
    $search: String
    $type: String
    $adGroupId: ID
    $brandId: ID
    $startDate: String
    $endDate: String
    $status: String
  ) {
    creatives(
      page: $page
      pageSize: $pageSize
      search: $search
      type: $type
      adGroupId: $adGroupId
      brandId: $brandId
      startDate: $startDate
      endDate: $endDate
      status: $status
    ) {
      creatives {
        id
        uniqueId
        description
        startDate
        endDate
        adGroupId
        brandId
        brandName
        tenantId
        createdBy
        createdAt
        updatedAt
        adGroup {
          id
          name
        }
        media {
          id
          creativeId
          fileName
          fileUrl
          format
          thumbnailUrl
          resolution
          frameRate
          duration
          createdAt
          updatedAt
        }
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_CREATIVE_BY_ID = gql`
  query GetCreativeById($id: ID!) {
    creative(id: $id) {
      id
      uniqueId
      description
      startDate
      endDate
      adGroupId
      brandId
      brandName
      tenantId
      createdBy
      createdAt
      updatedAt
      adGroup {
        id
        name
      }
      media {
        id
        creativeId
        fileName
        fileUrl
        format
        thumbnailUrl
        resolution
        frameRate
        duration
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_CREATIVE_USAGE = gql`
  query GetCreativeUsage($ids: [ID!]!) {
    creativeUsage(ids: $ids) {
      playlistCount
      screenCount
      playlists {
        id
        name
        type
      }
    }
  }
`;

const CREATE_CREATIVE = gql`
  mutation CreateCreative($input: CreateCreativeInput!) {
    createCreative(input: $input)
  }
`;

const UPDATE_CREATIVE = gql`
  mutation UpdateCreative($id: ID!, $input: UpdateCreativeInput!) {
    updateCreative(id: $id, input: $input)
  }
`;

const DELETE_CREATIVES = gql`
  mutation DeleteCreatives($ids: [ID!]!) {
    deleteCreatives(ids: $ids)
  }
`;

export const creativeService = {
  getAll: async (filters: CreativeFilters = {}): Promise<CreativesResponse> => {
    const { data } = await client.query({
      query: GET_CREATIVES,
      variables: filters,
      fetchPolicy: "network-only",
    });
    return data.creatives;
  },

  getById: async (id: string): Promise<Creative> => {
    const { data } = await client.query({
      query: GET_CREATIVE_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.creative;
  },

  getUsage: async (ids: string[]): Promise<CreativeUsage> => {
    const { data } = await client.query({
      query: GET_CREATIVE_USAGE,
      variables: { ids },
      fetchPolicy: "network-only",
    });
    return data.creativeUsage;
  },

  create: async (input: CreativeFormData): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_CREATIVE,
      variables: { input },
      context: {
        headers: {
          "Apollo-Require-Preflight": "true",
        },
      },
    });
    return data.createCreative;
  },

  update: async (payload: UpdateCreativePayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_CREATIVE,
      variables: {
        id: payload.id,
        input: payload.creative,
      },
      context: payload.creative.file
        ? {
            headers: {
              "Apollo-Require-Preflight": "true",
            },
          }
        : undefined,
    });
    return data.updateCreative;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_CREATIVES,
      variables: { ids },
    });
    return data.deleteCreatives;
  },
};
